import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import {
  FormBuilder,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subject, takeUntil } from 'rxjs';
import {
  RunTransition,
  RunTransitionWithDialog,
  WorkflowService,
} from 'src/app/shared/services/workflow/workflow.service';
import { requiredFileListUnlessProvidedMedia } from '../workflow-file-upload-validators';
import { PayeePopupComponent } from '../../payee-popup/payee-popup.component';
import { PayeeFormFieldComponent } from '../../payee-form-fields/payee-form-field/payee-form-field.component';
import { PayeeWorkflowProvidedMediaComponent } from '../../payee-workflow-provided-media/payee-workflow-provided-media.component';
import { PayeeDropzoneComponent } from '../../payee-dropzone/payee-dropzone.component';
import { PayeeIconComponent } from '../../payee-icon/payee-icon.component';
import { PayeeIconButtonComponent } from '../../payee-buttons/payee-icon-button/payee-icon-button.component';
import { PayeeButtonComponent } from '../../payee-buttons/payee-button/payee-button.component';

type DialogData = {
  transition_label: string;
};

@Component({
  selector: 'app-order-for-payment-external-id-received-dialog',
  templateUrl: './order-for-payment-external-id-received-dialog.component.html',
  styleUrls: ['./order-for-payment-external-id-received-dialog.component.scss'],
  imports: [
    PayeePopupComponent,
    FormsModule,
    ReactiveFormsModule,
    PayeeFormFieldComponent,
    PayeeWorkflowProvidedMediaComponent,
    PayeeDropzoneComponent,
    PayeeIconComponent,
    PayeeIconButtonComponent,
    PayeeButtonComponent,
  ],
})
export class OrderForPaymentExternalIdReceivedDialogComponent
  implements OnInit, OnDestroy
{
  readonly data = inject<RunTransitionWithDialog>(MAT_DIALOG_DATA);
  private dialogRef =
    inject<MatDialogRef<OrderForPaymentExternalIdReceivedDialogComponent>>(
      MatDialogRef,
    );
  private workflowService = inject(WorkflowService);
  private fb = inject(FormBuilder);

  private readonly destroy = new Subject<void>();
  loading = false;
  dialogData?: DialogData;

  readonly form = this.fb.group({
    case_id: this.fb.group({
      partA: this.fb.control('', {
        validators: [Validators.required, Validators.pattern(/^\d{5}$/)],
        nonNullable: true,
      }),
      partB: this.fb.control('', {
        validators: [Validators.required, Validators.pattern(/^\d{5}$/)],
        nonNullable: true,
      }),
      partC: this.fb.control('', {
        validators: [Validators.required, Validators.pattern(/^\d{4}$/)],
        nonNullable: true,
      }),
    }),
    files: this.fb.control<File[]>([], {
      validators: requiredFileListUnlessProvidedMedia(this.data.media),
      nonNullable: true,
    }),
  });

  get files() {
    return this.form.controls.files;
  }
  get case_id() {
    return this.form.controls.case_id;
  }
  get partA() {
    return this.case_id.controls.partA;
  }
  get partB() {
    return this.case_id.controls.partB;
  }
  get partC() {
    return this.case_id.controls.partC;
  }

  get dropzoneTitle(): string {
    if (!this.dialogData?.transition_label) {
      return '';
    }
    const parts = this.dialogData.transition_label.split('|');
    return parts.length > 1
      ? parts.slice(1).join('|').trim()
      : this.dialogData.transition_label;
  }

  @ViewChild('partA') partAInput?: ElementRef<HTMLInputElement>;
  @ViewChild('partB') partBInput?: ElementRef<HTMLInputElement>;
  @ViewChild('partC') partCInput?: ElementRef<HTMLInputElement>;

  async ngOnInit() {
    this.dialogData = (await this.workflowService.getDialogData({
      caseId: this.data.caseId,
      transitionId: this.data.transitionId,
    })) as DialogData;

    this.partA.valueChanges.pipe(takeUntil(this.destroy)).subscribe((v) => {
      const digits = v.replace(/\D/g, '');
      const formatted = digits.slice(0, 5);
      const remainder = digits.slice(5);
      if (remainder) {
        this.partB.patchValue(remainder + this.partB.value);
      }

      if (v !== formatted) {
        setTimeout(() => this.partA.patchValue(formatted), 0);
        return;
      }

      if (this.partA.valid) {
        this.partBInput?.nativeElement.focus();
      }
    });

    this.partB.valueChanges.pipe(takeUntil(this.destroy)).subscribe((v) => {
      const digits = v.replace(/\D/g, '');
      const formatted = digits.slice(0, 5);
      const remainder = digits.slice(5);
      if (remainder) {
        this.partC.patchValue(remainder + this.partC.value);
      }

      if (v !== formatted) {
        setTimeout(() => this.partB.patchValue(formatted), 0);
        return;
      }

      if (this.partB.valid) {
        this.partCInput?.nativeElement.focus();
      }
    });

    this.partC.valueChanges.pipe(takeUntil(this.destroy)).subscribe((v) => {
      const formatted = v.replace(/\D/g, '').slice(0, 4);
      if (v !== formatted) {
        setTimeout(() => this.partC.patchValue(formatted), 0);
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
  }

  onDrop(files: FileList) {
    const fileValues = [...this.files.value];
    for (let i = 0; i < files.length; ++i) {
      fileValues.push(files.item(i) as File);
    }
    this.files.setValue(fileValues);
  }

  removeFile(i: number) {
    const fileValues = [...this.files.value];
    fileValues.splice(i, 1);
    this.files.setValue(fileValues);
  }

  readonly onSubmit = async () => {
    this.form.markAllAsTouched();

    if (this.form.invalid) {
      return;
    }

    const {
      case_id: { partA, partB, partC },
      files,
    } = this.form.getRawValue();
    const externalId = `${partA}/Ü/${partB}/${partC}`;

    const closeValue: RunTransition = {
      caseId: this.data.caseId,
      transitionId: this.data.transitionId,
      params: {
        ...(this.data.extraParams ?? {}),
        files: [...(this.data.media ?? []).map((m) => m.uuid), ...files],
        external_id_value: externalId,
      },
    };

    this.loading = true;
    try {
      await this.workflowService.runTransition(closeValue);
      this.dialogRef.close(closeValue);
    } catch (error) {
      this.workflowService.showTransitionError(error);
    } finally {
      this.loading = false;
    }
  };

  readonly onCancel = () => {
    this.dialogRef.close();
  };
}
